import { memo, useCallback, useMemo, type MouseEvent } from "react";
import {
  buildDotMatrixCellKey,
  emptyDotMatrixCell,
  isDotMatrixCellVisuallyLit,
  resolveDotMatrixCellBackground,
} from "../../utils/dotMatrixUtils";
import type { DotMatrixProps } from "./types";
import { DEFAULT_DOT_MATRIX_COLS, DEFAULT_DOT_MATRIX_ROWS } from "./types";
import styles from "./DotMatrix.module.css";

function DotMatrixComponent({
  rows = DEFAULT_DOT_MATRIX_ROWS,
  cols = DEFAULT_DOT_MATRIX_COLS,
  matrixData = null,
  brightness = 100,
  disabled = false,
  selectedCells = [],
  selectedCornerIconSrc,
  cellSize = 32,
  cellGap = 6,
  fillContainer = false,
  className = "",
  onCellClick,
  onCellContextMenu,
}: DotMatrixProps) {
  const safeRows = Math.max(1, Math.floor(rows));
  const safeCols = Math.max(1, Math.floor(cols));
  const selectedSet = useMemo(() => new Set(selectedCells), [selectedCells]);

  const grid = useMemo(() => {
    const source = matrixData ?? [];
    return Array.from({ length: safeRows }, (_, row) =>
      Array.from(
        { length: safeCols },
        (_, col) => source[row]?.[col] ?? emptyDotMatrixCell()
      )
    );
  }, [matrixData, safeRows, safeCols]);

  const handleContextMenu = useCallback(
    (event: MouseEvent, row: number, col: number) => {
      event.preventDefault();
      if (disabled) return;
      onCellContextMenu?.(row, col, event);
    },
    [disabled, onCellContextMenu]
  );

  const rootClassName = [
    styles.root,
    fillContainer ? styles.rootFill : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName}>
      <div className={styles.grid} style={{ gap: cellGap }}>
        {grid.map((rowCells, row) => (
          <div className={styles.row} key={row} style={{ gap: cellGap }}>
            {rowCells.map((cell, col) => {
              const key = buildDotMatrixCellKey(row, col);
              const lit = isDotMatrixCellVisuallyLit(cell, brightness);
              const cellBackground = resolveDotMatrixCellBackground(
                cell,
                brightness
              );
              const selected = selectedSet.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    styles.cell,
                    lit ? styles.cellLit : "",
                    selected ? styles.cellSelected : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    width: cellSize,
                    height: cellSize,
                    ...(cellBackground ? { background: cellBackground } : {}),
                  }}
                  disabled={disabled}
                  onClick={(event) => onCellClick?.(row, col, event)}
                  onContextMenu={(event) => handleContextMenu(event, row, col)}
                  aria-label={`dot-matrix-${row}-${col}`}
                >
                  {selected && selectedCornerIconSrc ? (
                    <img
                      src={selectedCornerIconSrc}
                      alt=""
                      className={styles.cornerIcon}
                      draggable={false}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export const DotMatrix = memo(DotMatrixComponent);
